import { CertRiskBadge } from "@/components/CertRiskBadge";
import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Download, PenLine, BookOpen, CalendarCheck, CalendarDays, Globe, ExternalLink, CalendarRange, Calendar as CalendarIcon, KeyRound, Briefcase, UserCircle } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import GoogleCourseworkWidget from "@/components/GoogleCourseworkWidget";
import ParticipantCalendar from "@/components/ParticipantCalendar";
import ParticipantLicenses from "@/components/ParticipantLicenses";
import JobApplicationsPanel from "@/components/JobApplicationsPanel";
import { ClientCertificates } from "@/components/ClientCertificates";
import CvProfileSection from "@/components/CvProfileSection";
import { MyWbReports } from "@/components/WbAkteExtras";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// -------------------- i18n --------------------
const I18N = {
  de: {
    loading: "Lädt…",
    no_profile: "Kein Teilnehmer-Profil gefunden.",
    hello: (n) => `Hallo ${n}.`,
    class: "Klasse",
    ampel_title: "Dein Anwesenheitsstatus",
    ampel_green: "Alles im grünen Bereich",
    ampel_yellow: "Bitte aufpassen",
    ampel_red: "Kritisch — bitte melden",
    quota: "Quote",
    school_days: "Unterrichtstage",
    present: "Anwesend",
    absent: "Fehltage",
    unexcused: "Unentschuldigt",
    red_text: "Deine Anwesenheit liegt unter 75 % oder du hast mehrere unentschuldigte Fehltage. Bitte melde dich zeitnah bei deiner/deinem Dozent/-in.",
    yellow_text: "Deine Anwesenheit ist grenzwertig. Bitte achte darauf, jeden Unterrichtstag pünktlich zu erscheinen.",
    pending_title: (n) => `${n} Anwesenheit${n === 1 ? "" : "en"} noch nicht unterschrieben`,
    pending_sub: "Bitte bestätige deine Anwesenheit unten in der Historie.",
    tab_history: "Anwesenheitshistorie",
    tab_schedule: "Terminplan",
    tab_diary: "Klassenbuch",
    last_present: "Neuester Anwesenheitstag",
    download_pdf: "Aktuelle Monatsbescheinigung (PDF)",
    no_attendance: "Noch keine Anwesenheiten dokumentiert.",
    no_diary: "Noch keine Klassenbuch-Einträge.",
    no_upcoming: "Keine kommenden Unterrichtstage.",
    upcoming_intro: "Deine nächsten Unterrichtstage.",
    lecturer: "Dozent/-in",
    signed: "Unterschrieben",
    sign_btn: "Unterschreiben",
    no_sig_needed: "Keine Signatur nötig",
    lecturer_pending: "Dozent noch nicht signiert",
    sign_dialog_title: "Deine Unterschrift",
    sign_dialog_lead: (d) => `Bitte unterschreibe im Feld unten, um deine Anwesenheit am ${d} rechtsgültig zu bestätigen.`,
    sign_confirm_prefix: "Anwesenheit bestätigen ·",
    cancel: "Abbrechen",
    save: "Unterschreiben",
    saving: "Speichere…",
    topics: "Themen",
    contents: "Inhalte",
    incidents: "Vorkommnisse",
    status: {
      present: "Anwesend",
      sick: "Krank",
      excused: "Entschuldigt",
      unexcused: "Unentschuldigt",
    },
    diary_read_only: "Unterrichtsprotokoll für deine Klasse.",
    open_classroom: "Google Classroom öffnen",
    tab_plan: "Wochenplan",
    plan_empty: "Für diese Klasse ist noch kein Wochenplan hinterlegt.",
    plan_current: "Aktuell",
    plan_total: (n) => `${n} Wochen insgesamt`,
    tab_calendar: "Kalender",
    tab_licenses: "Meine Lizenzen",
    tab_jobs: "Job-Track-Liste",
    tab_cv: "Mein CV-Profil",
  },
  en: {
    loading: "Loading…",
    no_profile: "No participant profile found.",
    hello: (n) => `Hi ${n}.`,
    class: "Class",
    ampel_title: "Your attendance status",
    ampel_green: "All good",
    ampel_yellow: "Watch out",
    ampel_red: "Critical — please contact us",
    quota: "Rate",
    school_days: "Class days",
    present: "Present",
    absent: "Absences",
    unexcused: "Unexcused",
    red_text: "Your attendance is below 75 % or you have several unexcused absences. Please contact your lecturer as soon as possible.",
    yellow_text: "Your attendance is borderline. Please make sure to attend every class day on time.",
    pending_title: (n) => `${n} attendance day${n === 1 ? "" : "s"} still unsigned`,
    pending_sub: "Please confirm your attendance in the history below.",
    tab_history: "Attendance history",
    tab_schedule: "Schedule",
    tab_diary: "Class diary",
    last_present: "Latest day present",
    download_pdf: "Current monthly certificate (PDF)",
    no_attendance: "No attendance records yet.",
    no_diary: "No diary entries yet.",
    no_upcoming: "No upcoming class days.",
    upcoming_intro: "Your next class days.",
    lecturer: "Lecturer",
    signed: "Signed",
    sign_btn: "Sign",
    no_sig_needed: "No signature required",
    lecturer_pending: "Lecturer has not signed yet",
    sign_dialog_title: "Your signature",
    sign_dialog_lead: (d) => `Please sign below to legally confirm your attendance on ${d}.`,
    sign_confirm_prefix: "Confirm attendance ·",
    cancel: "Cancel",
    save: "Sign",
    saving: "Saving…",
    topics: "Topics",
    contents: "Content",
    incidents: "Incidents",
    status: {
      present: "Present",
      sick: "Sick",
      excused: "Excused",
      unexcused: "Unexcused",
    },
    diary_read_only: "Teaching log for your class.",
    open_classroom: "Open Google Classroom",
    tab_plan: "Weekly plan",
    plan_empty: "No weekly plan is set up for this class yet.",
    plan_current: "Current",
    plan_total: (n) => `${n} weeks total`,
    tab_calendar: "Calendar",
    tab_licenses: "My licenses",
    tab_jobs: "Job-Track-Liste",
    tab_cv: "My CV profile",
  },
};

const STATUS_INFO = {
  present: { cls: "bg-emerald-50 border-emerald-400 text-emerald-800" },
  sick: { cls: "bg-blue-50 border-blue-400 text-blue-800" },
  excused: { cls: "bg-amber-50 border-amber-400 text-amber-800" },
  unexcused: { cls: "bg-red-50 border-red-400 text-red-800" },
};

const LEVEL_ICON = { green: CheckCircle2, yellow: AlertTriangle, red: XCircle };
const LEVEL_STYLE = {
  green: "bg-emerald-50 border-emerald-500 text-emerald-900",
  yellow: "bg-amber-50 border-amber-500 text-amber-900",
  red: "bg-red-50 border-red-500 text-red-900",
};

const DE_MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DE_WEEKDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const EN_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDate(iso, lang) {
  if (!iso) return "—";
  if (lang === "de") return formatDeShort(iso);
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
}
function longDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  const wd = (lang === "de" ? DE_WEEKDAYS : EN_WEEKDAYS)[d.getDay()];
  const mo = (lang === "de" ? DE_MONTHS : EN_MONTHS)[d.getMonth()];
  return `${wd}, ${d.getDate()}. ${mo} ${d.getFullYear()}`;
}

export default function WeiterbildungParticipantHomePage() {
  const [lang, setLangState] = useState(() => localStorage.getItem("wb_lang") || "de");
  const setLang = (l) => { setLangState(l); localStorage.setItem("wb_lang", l); };
  const t = I18N[lang];

  const [me, setMe] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [diary, setDiary] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [tab, setTab] = useState("uebersicht");

  const load = async () => {
    setLoading(true);
    try {
      const [m, a, d, u] = await Promise.all([
        api.get("/weiterbildung/me"),
        api.get("/weiterbildung/me/attendance"),
        api.get("/weiterbildung/me/diary"),
        api.get("/weiterbildung/me/upcoming"),
      ]);
      setMe(m.data); setAttendance(a.data); setDiary(d.data); setUpcoming(u.data?.days || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitSignature = async () => {
    if (!signature || !signOpen?.signature_token) { toast.error(lang === "de" ? "Bitte unterschreiben" : "Please sign"); return; }
    setSigning(true);
    try {
      await api.post(`/weiterbildung/sign/${signOpen.signature_token}`, { signature });
      toast.success(lang === "de" ? "Unterschrieben — vielen Dank!" : "Signed — thank you!");
      setSignOpen(null); setSignature(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSigning(false); }
  };

  const pendingSignatures = useMemo(
    () => attendance.filter((a) => a.lecturer_signed && !a.already_signed && a.signature_token && a.status !== "unexcused"),
    [attendance]
  );
  const currentMonth = new Date().toISOString().slice(0, 7);

  if (loading) return <div className="p-8 text-xs text-slate-500">{t.loading}</div>;
  if (!me) return <div className="p-8 text-sm text-slate-500 italic">{t.no_profile}</div>;

  const { participant: p, class: cls, stats, quota, level } = me;
  const LvlIcon = LEVEL_ICON[level] || CheckCircle2;
  const lvlLabel = level === "green" ? t.ampel_green : level === "yellow" ? t.ampel_yellow : t.ampel_red;
  const daysAbsent = (stats.sick || 0) + (stats.excused || 0) + (stats.unexcused || 0);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="wb-participant-home">
      {/* Header with lang switch */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1">
            {cls?.measure_number} · {cls?.weiterbildung_name}
          </div>
          <h1 className="font-heading text-5xl font-light tracking-tighter text-slate-900">
            {t.hello(p.first_name)}
          </h1>
          <div className="text-sm text-slate-600 mt-2 font-mono">
            {t.class} <span className="text-slate-900 font-medium">{cls?.name || "—"}</span>
            {cls?.start_date && <> · {formatDate(cls.start_date, lang)} – {formatDate(cls.end_date, lang)}</>}
          </div>
          {cls?.google_classroom_url && (
            <a
              href={cls.google_classroom_url}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-3 py-2 border border-[#1a73e8] text-[#1a73e8] hover:bg-[#1a73e8] hover:text-white text-xs font-medium transition-colors"
              data-testid="participant-classroom-btn"
            >
              <ExternalLink size={12} strokeWidth={1.5} /> {t.open_classroom}
            </a>
          )}
        </div>
        <div className="flex items-center gap-1 border border-slate-200 p-1" data-testid="lang-switcher">
          <Globe size={14} strokeWidth={1.5} className="text-slate-400 ml-2 mr-1" />
          <button
            onClick={() => setLang("de")}
            className={`px-3 py-1.5 text-xs font-medium ${lang === "de" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="lang-de-btn"
          >DE</button>
          <button
            onClick={() => setLang("en")}
            className={`px-3 py-1.5 text-xs font-medium ${lang === "en" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="lang-en-btn"
          >EN</button>
        </div>
      </div>

      {/* Ampel */}
      <div className={`border-l-4 border-2 p-6 mb-8 ${LEVEL_STYLE[level] || LEVEL_STYLE.green}`} data-testid={`ampel-${level}`}>
        <div className="flex items-start gap-4">
          <LvlIcon size={36} strokeWidth={1.5} className="shrink-0 mt-1" />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-overline opacity-70">{t.ampel_title}</div>
            <div className="font-heading text-3xl font-light tracking-tighter mt-1">{lvlLabel}</div>
            <div className="mt-3 flex flex-wrap gap-6 font-mono text-sm">
              <div><span className="opacity-60">{t.quota}:</span> <b className="text-2xl">{quota !== null ? `${quota}%` : "—"}</b></div>
              <div><span className="opacity-60">{t.school_days}:</span> {stats.total || 0}</div>
              <div><span className="opacity-60">{t.present}:</span> {stats.present || 0}</div>
              <div><span className="opacity-60">{t.absent}:</span> {daysAbsent}</div>
              {stats.unexcused > 0 && <div className="text-red-700"><b>{t.unexcused}:</b> {stats.unexcused}</div>}
            </div>
            <div className="mt-3"><CertRiskBadge present={stats.present || 0} total={stats.total || 0} lang={lang} testId="participant-cert-risk" /></div>
            {level === "red" && <p className="text-sm mt-4 opacity-80">{t.red_text}</p>}
            {level === "yellow" && <p className="text-sm mt-4 opacity-80">{t.yellow_text}</p>}
          </div>
        </div>
      </div>

      {/* Pending banner */}
      {pendingSignatures.length > 0 && (
        <div className="border-l-4 border-slate-900 bg-slate-100 p-4 mb-8" data-testid="pending-signatures">
          <div className="flex items-center gap-3">
            <PenLine size={18} strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">{t.pending_title(pendingSignatures.length)}</div>
              <div className="text-xs text-slate-600 mt-0.5">{t.pending_sub}</div>
            </div>
          </div>
        </div>
      )}

      {/* Google Classroom Coursework */}
      {(cls?.google_classroom_url || cls?.classroom_course_id) && (
        <div className="mb-8">
          <GoogleCourseworkWidget classId={cls.class_id} lang={lang} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        <TabBtn testId="ptab-history" active={tab === "uebersicht"} onClick={() => setTab("uebersicht")} icon={CalendarCheck}>
          {t.tab_history} ({attendance.length})
        </TabBtn>
        <TabBtn testId="ptab-calendar" active={tab === "kalender"} onClick={() => setTab("kalender")} icon={CalendarIcon}>
          {t.tab_calendar}
        </TabBtn>
        <TabBtn testId="ptab-licenses" active={tab === "zugaenge"} onClick={() => setTab("zugaenge")} icon={KeyRound}>
          {t.tab_licenses}
        </TabBtn>
        <TabBtn testId="ptab-jobs" active={tab === "jobs"} onClick={() => setTab("jobs")} icon={Briefcase}>
          {t.tab_jobs}
        </TabBtn>
        <TabBtn testId="ptab-cv" active={tab === "cv"} onClick={() => setTab("cv")} icon={UserCircle}>
          {t.tab_cv}
        </TabBtn>
        <TabBtn testId="ptab-schedule" active={tab === "terminplan"} onClick={() => setTab("terminplan")} icon={CalendarDays}>
          {t.tab_schedule} ({upcoming.length})
        </TabBtn>
        <TabBtn testId="ptab-diary" active={tab === "klassenbuch"} onClick={() => setTab("klassenbuch")} icon={BookOpen}>
          {t.tab_diary} ({diary.length})
        </TabBtn>
        <TabBtn testId="ptab-plan" active={tab === "wochenplan"} onClick={() => setTab("wochenplan")} icon={CalendarRange}>
          {t.tab_plan} ({(cls?.weekly_plan || []).length})
        </TabBtn>
      </div>

      {tab === "uebersicht" && <MyWbReports />}
      {tab === "uebersicht" && (
        <div>
          {p?.participant_id && <div className="mb-6"><ClientCertificates clientId={p.participant_id} hideEmpty /></div>}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-xs text-slate-500 font-mono">
              {t.last_present}: {stats.last_present ? formatDate(stats.last_present, lang) : "—"}
            </div>
            <a
              href={`${API}/weiterbildung/participants/${p.participant_id}/monthly-pdf/${currentMonth}`}
              target="_blank" rel="noopener noreferrer"
              className="text-xs border border-slate-900 text-slate-900 px-4 py-2 hover:bg-slate-900 hover:text-white flex items-center gap-2"
              data-testid="download-monthly-pdf-btn"
            >
              <Download size={12} strokeWidth={1.5} /> {t.download_pdf}
            </a>
          </div>
          {attendance.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">{t.no_attendance}</div>
          ) : (
            <div className="border border-slate-200 divide-y divide-slate-100">
              {attendance.map((a) => {
                const si = STATUS_INFO[a.status] || { cls: "bg-slate-50 text-slate-800 border-slate-300" };
                const canSign = a.lecturer_signed && !a.already_signed && a.signature_token && a.status !== "unexcused";
                return (
                  <div key={a.date} className="p-4 flex items-center gap-4" data-testid={`att-row-${a.date}`}>
                    <div className={`px-3 py-2 border-l-2 text-xs font-medium min-w-[140px] ${si.cls}`}>
                      {t.status[a.status] || a.status}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{formatDate(a.date, lang)}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">
                        {a.time_from || "?"}–{a.time_to || "?"} · {t.lecturer}: {a.lecturer_name || "—"}
                      </div>
                    </div>
                    <div className="text-right">
                      {a.already_signed ? (
                        <div className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                          <CheckCircle2 size={14} strokeWidth={1.5} /> {t.signed}
                        </div>
                      ) : canSign ? (
                        <button
                          onClick={() => { setSignOpen(a); setSignature(null); }}
                          className="text-xs bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 flex items-center gap-1"
                          data-testid={`sign-btn-${a.date}`}
                        >
                          <PenLine size={12} strokeWidth={1.5} /> {t.sign_btn}
                        </button>
                      ) : a.status === "unexcused" ? (
                        <div className="text-[10px] text-slate-400 italic">{t.no_sig_needed}</div>
                      ) : !a.lecturer_signed ? (
                        <div className="text-[10px] text-slate-400 italic">{t.lecturer_pending}</div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "kalender" && (
        <div data-testid="participant-calendar-tab">
          <ParticipantCalendar lang={lang} classId={cls?.class_id} weeklyPlan={cls?.weekly_plan || []} />
        </div>
      )}

      {tab === "zugaenge" && (
        <div data-testid="participant-licenses-tab">
          <ParticipantLicenses lang={lang} />
        </div>
      )}

      {tab === "jobs" && (
        <div data-testid="participant-jobs-tab">
          <JobApplicationsPanel showExport={false} context="wb" />
        </div>
      )}

      {tab === "cv" && (
        <div data-testid="participant-cv-tab">
          <CvProfileSection />
        </div>
      )}

      {tab === "terminplan" && (
        <div data-testid="upcoming-schedule">
          <p className="text-xs text-slate-500 mb-4">{t.upcoming_intro}</p>
          {upcoming.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">{t.no_upcoming}</div>
          ) : (
            <div className="border border-slate-200 divide-y divide-slate-100">
              {upcoming.map((d) => (
                <div key={d.date} className="p-4 flex items-center gap-4" data-testid={`upcoming-${d.date}`}>
                  <div className="w-16 shrink-0 text-center border-r border-slate-100 pr-3">
                    <div className="text-[10px] uppercase text-slate-500">{(lang === "de" ? DE_WEEKDAYS : EN_WEEKDAYS)[new Date(d.date + "T00:00:00").getDay()]}</div>
                    <div className="font-heading text-2xl font-light">{d.date.slice(8, 10)}</div>
                    <div className="text-[10px] uppercase text-slate-500">{(lang === "de" ? DE_MONTHS : EN_MONTHS)[parseInt(d.date.slice(5, 7), 10) - 1].slice(0, 3)}</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-900">{longDate(d.date, lang)}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{d.time_from || "?"}–{d.time_to || "?"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "klassenbuch" && (
        <div>
          <p className="text-xs text-slate-500 mb-4">{t.diary_read_only}</p>
          {diary.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">{t.no_diary}</div>
          ) : (
            <div className="border border-slate-200 divide-y divide-slate-100">
              {diary.map((d) => (
                <div key={d.diary_id} className="p-4" data-testid={`diary-${d.date}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-mono text-sm">{formatDate(d.date, lang)} · {d.time_from || "?"}–{d.time_to || "?"}</div>
                    <div className="text-[10px] uppercase text-slate-400">{d.lecturer_name}</div>
                  </div>
                  {d.topics && <div className="text-sm"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.topics}:</span>{d.topics}</div>}
                  {d.contents && <div className="text-sm mt-1"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.contents}:</span>{d.contents}</div>}
                  {d.incidents && <div className="text-sm mt-1 text-amber-800"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">{t.incidents}:</span>{d.incidents}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "wochenplan" && (
        <div data-testid="participant-weekly-plan">
          {(cls?.weekly_plan || []).length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">{t.plan_empty}</div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4 font-mono">{t.plan_total(cls.weekly_plan.length)}</p>
              <div className="border border-slate-200 divide-y divide-slate-100">
                {cls.weekly_plan.map((w) => {
                  const isCurrent = w.start_date && w.end_date && w.start_date <= me.today && me.today <= w.end_date;
                  const isPast = w.end_date && w.end_date < me.today;
                  return (
                    <div
                      key={w.week_number}
                      className={`p-4 ${isCurrent ? "bg-[#e8f6f9] border-l-4 border-l-[#0a788c]" : isPast ? "opacity-60" : ""}`}
                      data-testid={`p-week-${w.week_number}`}
                    >
                      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                        <div className="flex items-baseline gap-3">
                          <div className="font-heading text-lg font-light tracking-tighter">
                            {lang === "en" ? "Week" : "Woche"} {w.week_number}
                          </div>
                          <div className="text-sm font-medium">{w.theme || "—"}</div>
                          {isCurrent && (
                            <span className="text-[10px] font-bold uppercase tracking-overline bg-[#0a788c] text-white px-2 py-0.5">
                              {t.plan_current}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">
                          {formatDate(w.start_date, lang)} – {formatDate(w.end_date, lang)}
                        </div>
                      </div>
                      {(w.topics || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(w.topics || []).map((tp, i) => (
                            <span key={i} className="text-[11px] px-2 py-1 bg-slate-900 text-white">{tp}</span>
                          ))}
                        </div>
                      )}
                      {(w.subtopics || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(w.subtopics || []).map((tp, i) => (
                            <span key={i} className="text-[11px] px-2 py-1 border border-slate-300 text-slate-700 bg-white">{tp}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}


      {/* Signature Dialog */}
      <Dialog open={!!signOpen} onOpenChange={(o) => !o && setSignOpen(null)}>
        <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
            <div className="text-xs uppercase text-slate-500">{t.sign_confirm_prefix} {signOpen && formatDate(signOpen.date, lang)}</div>
            <DialogTitle className="font-heading text-2xl font-light tracking-tighter">{t.sign_dialog_title}</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4">
            <p className="text-xs text-slate-600">{signOpen && t.sign_dialog_lead(formatDate(signOpen.date, lang))}</p>
            <SignaturePad value={signature} onChange={setSignature} />
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100">
            <button onClick={() => setSignOpen(null)} className="px-4 py-2 text-sm border border-slate-300">{t.cancel}</button>
            <button
              onClick={submitSignature}
              disabled={signing || !signature}
              className="px-4 py-2 text-sm bg-slate-900 text-white disabled:opacity-50"
              data-testid="submit-signature-btn"
            >
              {signing ? t.saving : t.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
        active ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      <Icon size={14} strokeWidth={1.5} /> {children}
    </button>
  );
}
